export const MEDIA_TYPES = {
  BULLETIN: 'bulletin',
  HANDOUT: 'handout',
  CHILDRENS_HANDOUT: 'childrensHandout',
} as const;

export type TMediaType = (typeof MEDIA_TYPES)[keyof typeof MEDIA_TYPES];

export const CHILDRENS_HANDOUT_START_DATE = new Date(2026, 2, 29);

/**
 * The first Sunday whose bulletin comes from the internal planning system
 * (`bulletins/{date}`) instead of the external PDF on cpmfiles1.com.
 *
 * Sundays before this date: bulletin buttons link to the CDN PDF.
 * Sundays on or after this date: bulletin buttons link to /bulletin/:date.
 *
 * Keep in sync with `INTERNAL_BULLETIN_START_DATE` in
 * `functions/src/ingestRSSFeeds.ts`.
 */
export const INTERNAL_BULLETIN_START_DATE = '2026-08-30';

export const ROUTES = {
  LANDING: '/',
  SUNDAY: '/sunday',
  SERVICE_DETAIL: '/sunday/service/:date',
  FRESH_BREAD: '/podcasts/fresh-bread',
  EQUIPPING_HOUR: '/podcasts/equipping-hour',
  PODCAST_DETAIL: '/podcasts/:podcastSlug/:episodeSlug',
  SETTINGS: '/settings',
  PLANNING: '/planning',
  PLANNING_SONGS: '/planning/songs',
  PLANNING_SONG: '/planning/songs/:songId',
  PLANNING_PEOPLE: '/planning/people',
  PLANNING_SLIDES: '/planning/slides',
  PLANNING_MASTER_PLAN: '/planning/master-plan',
  PLANNING_EDITOR: '/planning/:date',
  BULLETIN: '/bulletin/:date',
  HANDOUT: '/handout/:date/:handoutId',
} as const;

/**
 * The classification the hymnal uses, from its table of contents.
 *
 * A hymn carries exactly one topic: the topic its number range implies. A
 * planner tags a custom song by hand, and a custom song can carry several
 * topics.
 *
 * To add a topic, change this list. That change is deliberate and takes one
 * line. A fixed list keeps the topic filter reliable, because nobody can add a
 * topic with a typo.
 */
export const HYMNAL_TOPICS = [
  'Adoration and Praise',
  'Confession',
  'Confidence and Comfort',
  "Christ's Incarnation",
  'Gospel Grace',
  "Christ's Resurrection",
  "Christ's Ascension",
  "Christ's Second Coming",
  'Eternity',
  'Commitment and Consecration',
  'The Church',
  "God's Word",
  'The Holy Spirit',
  'Thanksgiving',
  'Children',
] as const;

/**
 * The church identity that the bulletin cover and the page footers show. This
 * data almost never changes. Therefore it lives in config, and no
 * service-plan document holds a copy of it.
 */
export const CHURCH_INFO = {
  name: 'Grace Bible Church',
  locality: 'Gainesville',
  address: '1750 NW 80th Blvd, Gainesville, FL 32606',
  website: 'gracegainesville.org',
  calendarUrl: 'gracegainesville.org/calendar',
  pastor: 'Brandon Phillips',
  associatePastor: 'Keith Kemp',
  serviceTime: '10:30 AM',
} as const;

/**
 * The parts of the worship service a person can be tagged with, and the parts
 * the Line Up tab asks a planner to fill.
 *
 * The labels are the ones the printed bulletin already uses. They come from the
 * same vocabulary as ORDER_ITEM_HEADINGS in BulletinView.utils.ts, so a planner
 * reads one set of words across the editor, the roster, and the page itself.
 *
 * `preacher` and `worshipLeader` are the two that are not order-of-service
 * items: they live on `sermon.preacher` and `worship.leader`. They are named
 * after the person rather than the part, because that is what everybody at
 * Grace already calls them.
 *
 * Order items of type `songs` and `custom` also carry a person, and
 * PersonPicker handles those with the same control. Neither is listed here: a songs credit is
 * for special music sung by somebody other than the worship leader, and a
 * custom item is named by the planner at the time. Slots with no role key list
 * everyone alphabetically instead of surfacing a group.
 *
 * To add a role, add a line. A fixed list keeps the tagging reliable, because
 * nobody can introduce a role with a typo.
 */
export const SERVICE_ROLES = [
  { key: 'preacher', label: 'Preacher' },
  { key: 'worshipLeader', label: 'Worship Leader' },
  { key: 'callToWorship', label: 'Call to Worship' },
  { key: 'scriptureReading', label: 'Scripture Reading' },
  { key: 'prayerOfResponse', label: 'Prayer of Response' },
  { key: 'communion', label: 'Communion' },
  { key: 'benediction', label: 'Benediction' },
  { key: 'announcements', label: 'Announcements' },
] as const;

export type TServiceRoleKey = (typeof SERVICE_ROLES)[number]['key'];

/** The label lookup and the Firestore read guard for one keyed `{ key, label }` list. */
function keyedLabels<TKey extends string>(
  list: readonly { key: TKey; label: string }[]
): {
  label: (key: TKey) => string;
  isKey: (value: unknown) => value is TKey;
} {
  const labels = Object.fromEntries(list.map((entry) => [entry.key, entry.label])) as Record<
    TKey,
    string
  >;
  return {
    label: (key) => labels[key],
    isKey: (value): value is TKey => typeof value === 'string' && value in labels,
  };
}

const serviceRoles = keyedLabels(SERVICE_ROLES);

/** The reader-facing name of a service role, e.g. `callToWorship` to "Call to Worship". */
export const serviceRoleLabel = serviceRoles.label;

/** Type guard for a raw string read back from Firestore. */
export const isServiceRoleKey = serviceRoles.isKey;

/**
 * Roles a person can be tagged with that are NOT part of the worship service:
 * the people who coordinate a week rather than take part in it. Kept apart from
 * SERVICE_ROLES so the Line Up and the order of service never list them.
 */
export const COORDINATOR_ROLES = [{ key: 'planningLead', label: 'Planning lead' }] as const;

export type TCoordinatorRoleKey = (typeof COORDINATOR_ROLES)[number]['key'];

/** Every role a person can be tagged with: a service role or a coordinator role. */
export type TPersonRoleKey = TServiceRoleKey | TCoordinatorRoleKey;

const coordinatorRoles = keyedLabels(COORDINATOR_ROLES);
const personRoles = keyedLabels<TPersonRoleKey>([...SERVICE_ROLES, ...COORDINATOR_ROLES]);

/** Type guard for a coordinator role key. */
export const isCoordinatorRoleKey = coordinatorRoles.isKey;

/** The reader-facing name of any person role, service or coordinator. */
export const personRoleLabel = personRoles.label;

/** Type guard for any person role key read back from Firestore. */
export const isPersonRoleKey = personRoles.isKey;

/** Grouped options for a role select: the service roles, then the coordinator roles. */
export const PERSON_ROLE_SELECT_DATA: {
  group: string;
  items: { value: TPersonRoleKey; label: string }[];
}[] = [
  {
    group: 'Service',
    items: SERVICE_ROLES.map((role) => ({ value: role.key, label: role.label })),
  },
  {
    group: 'Coordination',
    items: COORDINATOR_ROLES.map((role) => ({ value: role.key, label: role.label })),
  },
];

/**
 * What kind of occasion a master plan date is. Most dates are Sundays. A
 * retreat weekend (Friday to Sunday) and a special service (Christmas Eve)
 * are the other two, which is why the schedule takes any calendar date.
 */
export const SERVICE_TYPES = [
  { key: 'sunday', label: 'Sunday service' },
  { key: 'special', label: 'Special service' },
  { key: 'retreat', label: 'Retreat' },
] as const;

export type TServiceType = (typeof SERVICE_TYPES)[number]['key'];

const serviceTypes = keyedLabels(SERVICE_TYPES);

/** The reader-facing name of an occasion type, e.g. `retreat` to "Retreat". */
export const serviceTypeLabel = serviceTypes.label;

/** Type guard for a raw string read back from Firestore. */
export const isServiceType = serviceTypes.isKey;

/**
 * The two decided states of a schedule assignment, as the master spreadsheet
 * used them. Undecided is `null` on the model rather than a third member, so
 * a select can clear back to it.
 */
export const ASSIGNMENT_STATUSES = [
  { key: 'tentative', label: 'Tentative' },
  { key: 'scheduled', label: 'Scheduled' },
] as const;

export type TAssignmentStatus = (typeof ASSIGNMENT_STATUSES)[number]['key'];

const assignmentStatuses = keyedLabels(ASSIGNMENT_STATUSES);

/** The reader-facing name of an assignment status, e.g. `scheduled` to "Scheduled". */
export const assignmentStatusLabel = assignmentStatuses.label;

/** Type guard for a raw string read back from Firestore. Null is not a status. */
export const isAssignmentStatus = assignmentStatuses.isKey;

/** The most structured refs one scripture value can hold. Matches the passage cap in firestore.rules. */
export const MAX_SCRIPTURE_REFS = 10;

/** The longest scripture freeform text. Matches the passage cap in firestore.rules. */
export const MAX_SCRIPTURE_FREEFORM_LENGTH = 200;

/** The most equipping classes one date can hold. Matches the equipping list cap in firestore.rules. */
export const MAX_EQUIPPING_CLASSES = 10;

/** The longest equipping class title; a client-side cap, since rules cannot validate list elements. */
export const MAX_EQUIPPING_TITLE_LENGTH = 200;

/** The equipping class categories a church starts with, before a planner adds one. */
export const DEFAULT_EQUIPPING_CATEGORIES: readonly string[] = [
  'Churchwide',
  'Men',
  'Women',
  'Children',
];
